import React, { useState, useEffect } from 'react';
import PieChart from './PieChart';
import './PieChart.scss';
import { useMemo } from 'react';


interface ChartSegment {
  label: string;
  count: number;
  filter: string;
}

interface ChartData {
  title: string;
  segments: ChartSegment[];
}

interface DashboardChartsProps {
  chartsData?: ChartData[];
  onSliceClick?: (chartTitle: string, category: string, filter: string, isActive: boolean) => void;

   // NEW
  variant?: 'desktop' | 'mobile';
  activeCategory?: string | null;
}


const DashboardCharts: React.FC<DashboardChartsProps> = ({ 
  chartsData, 
  onSliceClick,
  variant = 'desktop',
  activeCategory = null,
}) => {


    // NEW: globally active slice (across all charts)
  const [activeSlice, setActiveSlice] = useState<{
    chartTitle: string;
    category: string;
  } | null>(null);

  // Handle slice selection across all charts
const handleSliceClick = (chartTitle: string, category: string, isActive: boolean) => {
  // console.log(`📊 Dashboard: ${chartTitle} - ${category} ${isActive ? 'activated' : 'deactivated'}`);
  
  // Find the filter for this category
  const chart = chartsData?.find(c => c.title === chartTitle);
  const segment = chart?.segments.find(s => s.label === category);
  const filter = segment?.filter || '';
  
  // Call parent callback with filter information
  if (onSliceClick) {
    onSliceClick(chartTitle, category, filter, isActive);
  }
};



  // Convert API data to PieChart format
  const convertToPieChartData = (chart: ChartData) => {
    // Color mapping based on chart title + label combination
    const getColorForCategory = (chartTitle: string, label: string, index: number) => {
      const colorMap: {[key: string]: string} = {
        // Communications colors
        'Communications.Good': '#00B050',
        'Communications.Bad': '#FF0000',
        
        // Current States colors
        'Current states.Open': '#44B3E1',
        'Current states.Closed': '#104861',
        'Current states.Afterflow': '#12B76A',
        'Current states.M/H Open': '#C0E6F5',
        'Current states.M/H/S Close': '#C00000',
        'Current states.Shut In': '#FF0000',
        'Current states.Recovery': '#EF6820',
        
        // Plunger Velocity colors (different from Communications.Good)
        'Plunger velocity.Fast': '#44B3E1',
        'Plunger velocity.Good': '#12B76A', // Different green for Plunger velocity
        'Plunger velocity.Slow': '#EF6820',
        'Plunger velocity.None': '#FF0000',
        
        // Alarms colors
        'Alarms.OK': '#00B050',
        'Alarms.Cons. Non Arrival SD': '#C00000',
        'Alarms.ESD': '#FF0000',
        'Alarms.High LP Max': '#EF6820',
        'Alarms.Modbus Error': '#FFFF00',
        'Alarms.Valve Error/Failure': '#C0E6F5',
        'Alarms.Other': '#E49EDD',
        
        // Firmware colors - will use fallback colors since there are many versions
      };

      // Create key from chart title and label
      const colorKey = `${chartTitle}.${label}`;
      
      // Return specific color if found
      if (colorMap[colorKey]) {
        return colorMap[colorKey];
      }

      // Fallback colors for firmware versions and other unlisted combinations
      const fallbackColors = [
        '#12B76A',
        '#C0E6F5',
        '#E49EDD',
        '#44B3E1',
        '#EF6820', 
        '#104861',
      ];
      return fallbackColors[index % fallbackColors.length];
    };

    // Filter out segments with count 0 and convert to PieChart format
    return chart.segments
      .filter(segment => segment.count > 0) // Only include segments with count > 0
      .map((segment, index) => ({
        category: segment.label,
        value: segment.count,
        color: getColorForCategory(chart.title, segment.label, index)
      }));
  };

  // Calculate total for each chart (only counting non-zero segments)
  const calculateTotal = (chart: ChartData) => {
    return chart.segments
      .filter(segment => segment.count > 0)
      .reduce((total, segment) => total + segment.count, 0);
  };

  // Check if chart has any non-zero data
  const hasNonZeroData = (chart: ChartData) => {
    return chart.segments.some(segment => segment.count > 0);
  };

  // Check if we have valid API data
  const hasValidApiData = chartsData && Array.isArray(chartsData) && chartsData.length > 0;

  // Show loading state if no data at all
  if (!hasValidApiData) {
    return (
      <div className="dashboard-charts-container">
        <div className="charts-loading">
          <div className="loading-message">Loading dashboard charts...</div>
        </div>
      </div>
    );
  }

    const chartsWithData = chartsData.filter(chart => hasNonZeroData(chart));


  // If no charts have data, show a message
  if (chartsWithData.length === 0) {
    return (
      <div className="dashboard-charts-container">
        <div className="no-data-message">
          <div className="message">No data available for dashboard charts</div>
        </div>
      </div>
    );
  }

  return (
<div className="dashboard-charts-container">
  {chartsWithData.map((chart, index) => {
    const isChartActive = activeSlice?.chartTitle === chart.title;
    const activeCategory =
      isChartActive ? activeSlice?.category ?? null : null;

    return (
      <PieChart
        key={`${chart.title}-${index}`}
        title={chart.title}
        data={convertToPieChartData(chart)}
        totalValue={calculateTotal(chart)}
        className={`${chart.title.toLowerCase().replace(/\s+/g, '-')}-chart`}
        activeCategory={activeCategory}
        variant={variant}
        onSliceClick={(categoryOrNull: string | null) => {
          if (!onSliceClick) return;

          // 🔴 CASE 1: slice was active and user clicked it again → DEACTIVATE
          if (categoryOrNull === null) {
            // clear global active slice
            setActiveSlice(null);

            // send nulls to API so table resets
            onSliceClick(chart.title, null, null, false);

            return;
          }

          // 🟢 CASE 2: user clicked a slice (maybe from another chart)
          const category = categoryOrNull;

          // update global active slice (this will visually deactivate any old one)
          setActiveSlice({ chartTitle: chart.title, category });

          // get filter for this category
          const segment = chart.segments.find(s => s.label === category);
          const filter = segment?.filter || null; // or '' if your API expects empty string

          // send new filter to API
          onSliceClick(chart.title, category, filter, true);
        }}
      />
    );
  })}
</div>


  );
};

export default DashboardCharts;
